import React, { useState, useMemo, useEffect } from 'react';
import { leadApi, apiClient } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { RotateCcw, User, ArrowRight } from 'lucide-react';

const REVIVE_STAGES = [
    { id: 'new_lead', label: 'New Lead' },
    { id: 'call_back', label: 'Call Back' },
    { id: 'warm_lead', label: 'Warm Lead' },
    { id: 'hot_lead', label: 'Hot Lead' },
    { id: 'in_progress', label: 'In Progress' },
];

/**
 * ReactivateLeadModal
 * ---------------------
 * Opens when a super_admin or sales_manager tries to move a `rejected` /
 * `not_interested` lead back into the active pipeline. Forces them to
 * pick a new owner (sales_executive / team_leader / sales_manager) and
 * an active target stage. Calls POST /api/leads/{id}/reactivate.
 */
export default function ReactivateLeadModal({ open, lead, defaultStage, onClose, onSuccess }) {
    const [agents, setAgents] = useState([]);
    const [stage, setStage] = useState(defaultStage || 'new_lead');
    const [teamFilter, setTeamFilter] = useState('all');
    const [assigneeId, setAssigneeId] = useState('');
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setStage(defaultStage || 'new_lead');
        setAssigneeId('');
        setNotes('');
        (async () => {
            try {
                const res = await apiClient.get('/users?roles=sales_executive,team_leader,sales_manager');
                const list = (res.data || []).filter(u => u.is_active !== false);
                setAgents(list);
            } catch (e) {
                toast.error('Failed to load agents');
            }
        })();
    }, [open, defaultStage]);

    const teams = useMemo(() => {
        const names = new Set();
        agents.forEach(a => { if (a.team_name) names.add(a.team_name); });
        return Array.from(names).sort();
    }, [agents]);

    const filteredAgents = useMemo(() => {
        const roleOrder = { sales_manager: 0, team_leader: 1, sales_executive: 2 };
        return agents
            .filter(a => teamFilter === 'all' || a.team_name === teamFilter)
            .sort((a, b) => (roleOrder[a.role] ?? 9) - (roleOrder[b.role] ?? 9) || (a.full_name || '').localeCompare(b.full_name || ''));
    }, [agents, teamFilter]);

    const handleConfirm = async () => {
        if (!assigneeId) {
            toast.error('Please choose an agent to hand this lead to');
            return;
        }
        if (!stage) {
            toast.error('Please choose a target stage');
            return;
        }
        setSaving(true);
        try {
            await leadApi.reactivate(lead.id, {
                new_stage: stage,
                new_assigned_to: assigneeId,
                notes: notes.trim(),
            });
            const agent = agents.find(a => a.id === assigneeId);
            toast.success(`Reactivated & assigned to ${agent?.full_name || 'agent'}`);
            if (onSuccess) onSuccess();
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to reactivate lead');
        } finally {
            setSaving(false);
        }
    };

    if (!lead) return null;

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-lg" data-testid="reactivate-lead-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <RotateCcw className="h-5 w-5 text-emerald-500" />
                        Reactivate Lead & Reassign
                    </DialogTitle>
                    <DialogDescription>
                        Bring <span className="font-semibold text-foreground">{lead.full_name}</span> back into the pipeline and hand it to a specific agent.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    <div className="p-3 rounded-md bg-muted/40 text-sm space-y-1">
                        <div><span className="text-muted-foreground">Phone: </span><span className="font-medium">{lead.phone || '—'}</span></div>
                        <div><span className="text-muted-foreground">Current: </span><span className="font-medium capitalize">{(lead.stage || '').replace('_', ' ')}</span>{lead.rejection_reason && <> · <span className="text-muted-foreground">reason:</span> <span className="italic">{lead.rejection_reason}</span></>}</div>
                        {lead.assigned_to_name && (
                            <div><span className="text-muted-foreground">Previously with: </span><span className="font-medium">{lead.assigned_to_name}</span></div>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label>Move to Stage</Label>
                        <Select value={stage} onValueChange={setStage}>
                            <SelectTrigger data-testid="reactivate-stage-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {REVIVE_STAGES.map(s => (
                                    <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {teams.length > 0 && (
                        <div className="space-y-2">
                            <Label>Filter Agents By Team</Label>
                            <Select value={teamFilter} onValueChange={setTeamFilter}>
                                <SelectTrigger data-testid="reactivate-team-filter"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Teams</SelectItem>
                                    {teams.map(t => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    <div className="space-y-2">
                        <Label>Assign To *</Label>
                        <Select value={assigneeId} onValueChange={setAssigneeId}>
                            <SelectTrigger data-testid="reactivate-assignee-select">
                                <SelectValue placeholder="Pick an agent" />
                            </SelectTrigger>
                            <SelectContent className="max-h-72">
                                {filteredAgents.length === 0 && (
                                    <div className="px-3 py-2 text-sm text-muted-foreground">No agents match this team filter</div>
                                )}
                                {filteredAgents.map(a => (
                                    <SelectItem key={a.id} value={a.id} data-testid={`reactivate-agent-${a.id}`}>
                                        <span className="flex items-center gap-2">
                                            <User className="h-3.5 w-3.5 opacity-60" />
                                            <span>{a.full_name}</span>
                                            <span className="text-xs text-muted-foreground">· {(a.role || '').replace('_', ' ')}</span>
                                            {a.team_name && <span className="text-xs text-muted-foreground">· {a.team_name}</span>}
                                        </span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-2">
                        <Label>Notes (optional)</Label>
                        <Textarea
                            placeholder="Why are we reviving this lead?"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={2}
                            data-testid="reactivate-notes-input"
                        />
                    </div>
                </div>

                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={onClose} disabled={saving} data-testid="reactivate-cancel-btn">Cancel</Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={saving || !assigneeId}
                        className="gap-2"
                        data-testid="reactivate-confirm-btn"
                    >
                        {saving ? 'Reactivating…' : (<>Reactivate <ArrowRight className="h-4 w-4" /></>)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
